import type { Data } from "@measured/puck";
import type { PuckPageRecord, PuckPageStatus } from "@/lib/cms-puck/types";
import { hydrate } from "@/api/cms-puck/content-i18n";

/**
 * Couche d'accès au CMS avancé — branchée sur l'API NestJS (`/back/api/cms/advanced`).
 * L'instance axios `api` injecte déjà Authorization + Instance → scoping tenant
 * garanti côté serveur (@CmsData). Identité d'une page = son `id` (uuid).
 *
 * Ce module regroupe les internes PARTAGÉS entre sous-domaines (pages, flows…) :
 * il n'est PAS ré-exporté par le barrel `api.ts` (surface publique inchangée).
 */

export const BASE = "back/api/cms/advanced";

/** Forme renvoyée par l'API (layout = doc CMS avancé neutre ; strings = dict langue). */
export interface PuckPageApi {
  id: string;
  name: string;
  status: PuckPageStatus;
  /** Clé du modèle de bibliothèque dont la page est issue (null = vierge). */
  sourceTemplateKey?: string | null;
  layout: Data;
  publishedLayout: Data | null;
  updatedAt: string;
  /** Présent uniquement quand on charge avec `?lang=` (cf. getPage côté API). */
  strings?: Record<string, string>;
  catalogFlowRoles?: PuckPageRecord["catalogFlowRoles"];
  memberFlowRoles?: PuckPageRecord["memberFlowRoles"];
  shellFlowRoles?: PuckPageRecord["shellFlowRoles"];
  searchFlowRoles?: PuckPageRecord["searchFlowRoles"];
  isMaintenancePage?: boolean;
  isAbonnementPage?: boolean;
  isSelectionCategoryDetailPage?: boolean;
}

/**
 * Convertit la réponse API en record BO. Le `layout` renvoyé est NEUTRE (texte
 * vidé) ; si `strings` est fourni, on le réhydrate pour l'édition WYSIWYG.
 */
export function toRecord(apiPage: PuckPageApi): PuckPageRecord {
  const layout = apiPage.strings
    ? hydrate(apiPage.layout, apiPage.strings)
    : apiPage.layout;
  return {
    id: apiPage.id,
    name: apiPage.name,
    status: apiPage.status,
    sourceTemplateKey: apiPage.sourceTemplateKey ?? null,
    data: layout,
    updatedAt: apiPage.updatedAt,
    strings: apiPage.strings,
    // Base média (partagée) = layout neutre avant réhydratation ; sert au save
    // des images par langue (base + surcharges).
    neutralLayout: apiPage.layout,
    catalogFlowRoles: apiPage.catalogFlowRoles,
    memberFlowRoles: apiPage.memberFlowRoles,
    shellFlowRoles: apiPage.shellFlowRoles,
    searchFlowRoles: apiPage.searchFlowRoles,
    isMaintenancePage: apiPage.isMaintenancePage,
    isAbonnementPage: apiPage.isAbonnementPage,
    isSelectionCategoryDetailPage: apiPage.isSelectionCategoryDetailPage,
  };
}
